import _ from 'lodash'
import {
  getScopedFieldNameByShipType,
  calculateTotalVolume,
  hasDuplicateSpec,
  ableToSkipValidation,
  isSkipRequired,
  getPriceCap,
  getActiveTransientData,
  getLengthUsedByFirstLayer,
  getSpecItemExceedLength,
  signedApplianceContract,
  getAttrTransientKey,
  getFieldLengthMaxByName,
  getProductNameLengthMax,
  determineSpecLevel
} from '../util'
import Validators, { numericality, length } from 'redux-form-validators'
import {
  DEADLINE_OPTIONS,
  PRE_ORDER_EXPECTED_SHIP_DATE_OPTIONS,
  PRE_ORDER_EXPECTED_SHIP_DATE_SUPPLIER_OPTIONS,
  ON_SHELVED_TS_OPTIONS
} from '../../../../Shared'
import { isValidBarcode, isNewListingProposal } from '../../../../../../../lib/proposalHelper'
import { getShipTypeIdFromAllValues } from '../../../../../../lib/ReduxFormSelectors/ProposalListingForm'
import { getDollarFormatted } from '../../../../../../lib/FormHelper'
import { isEmptyStr, isMomentOutdated } from '../../../../../../lib/Validator'
import { calculateDaysFromDateType } from '../../../../../../lib/dateUtil'
import {
  FEATURE_TITLE_INVALID_REGEX,
  PRODUCT_SHIP_TYPE,
  FIELD_MAX_NUM_VAL,
  SUBSTATION_ID,
  LARGE_VOLUME_RULE,
  PRE_ORDER_EXPECTED_SHIP_DATE
} from '@vzmi/shp_shared_compo/constants/proposal'
import { checkSuppilerIncludedPreOrder } from '@vzmi/shp_shared_compo/helpers/proposalHelpers'
import { getDefaultTimezoneMoment } from '../../../../../../../lib/renderUtil'
import {
  SPEC_ATTRIBUTE_VALIDATE_RULE_TEXT,
  CATEGORY_TYPE,
  PRODUCT_VOLUME_DIMENSION_NAMES,
  PRODUCT_VOLUME_DIMENSION
} from '../../../../../../constants/proposalTypes'
import { getExternalImages } from '@vzmi/shp_shared_compo'
import { FORM_ERROR_CLASS_NAME } from '../../../../../../styles/variables'

Object.assign(Validators.messages, {
  presence: '不可為空',
  tooShort: '最少輸入{count, number}個字',
  tooLong: '最多輸入{count, number}個字',
  lessThan: '不可少於{count, number}'
})

export const validateFlow = (...validations) => (value, allValues, props, name) => {
  let error
  for (const validation of validations) {
    if (error !== undefined) { return error }
    error = validation(value, allValues)
  }

  return error
}

/** return validateFlow and use ableToSkipValidation to skip validation when allValues SKIP_REQUIRED_FIELD is true but Field 還沒有值的時候 */
export const skipRequiredThenValidate = (...validations) => (value, allValues, props, name) => {
  if (ableToSkipValidation(value, allValues)) { return undefined }
  return validateFlow(...validations)(value, allValues, props, name)
}

/**
 * 驗證最大值上限 limit
 * 錯誤訊息固定為 `上限為 ${limit}`
 */
export const validateUnderOrEqual = limit => numericality({
  '<=': limit,
  msg: `上限為 ${getDollarFormatted(limit)}`
})

// 一般數字 input 的上限為 999,999
export const inputNumberLimit = (valueInStore, allValues) => {
  const numberLimit = FIELD_MAX_NUM_VAL.NOT_SPECIFIED
  return validateUnderOrEqual(numberLimit)(valueInStore)
}

// 長寬高的上限值為 99,999
export const sizeDimensionNumberLimit = (valueInStore, allValues) => {
  const dimensionLimit = FIELD_MAX_NUM_VAL.SIZE_DIMENSION
  return validateUnderOrEqual(dimensionLimit)(valueInStore)
}

// 重量上限為 2147483647
export const weightNumberLimit = (valueInStore, allValues) => {
  const weightLimit = FIELD_MAX_NUM_VAL.WEIGHT
  return validateUnderOrEqual(weightLimit)(valueInStore)
}

// field input 長度限制的 validation，預設錯誤訊息：字數超過上限
export const lengthMax = fieldName => (valueInStore, allValues) => {
  const max = getFieldLengthMaxByName(fieldName)
  return length({ max, msg: '字數超過上限' })(valueInStore)
}

// 成本 <= 購物中心售價 <= 市價
// product.cost <= listing.price <= product.msrp
const getMsrpPriceCost = allValues => {
  const { product = {}, listing = {} } = allValues
  const { msrp, cost } = product
  const { price } = listing
  return {
    msrp: _.toNumber(msrp),
    price: _.toNumber(price),
    cost: _.toNumber(cost)
  }
}

/**
 * 直接從 allValue 中判斷是否為 newListingProposal,
 */
const getIsNewListing = (allValues = {}) => {
  const proposalType = _.get(allValues, 'setting.proposalType', '')
  return isNewListingProposal(proposalType)
}

export const msrpAmountValidation = (valueInStore, allValues) => {
  const { msrp, price, cost } = getMsrpPriceCost(allValues)
  const isNewListing = getIsNewListing(allValues)
  if (isNewListing && msrp < price) { return '需高於購物中心售價' }
  if (msrp < cost) { return '需高於進貨成本' }
  return undefined
}

export const priceAmountValidation = (valueInStore, allValues) => {
  const { msrp, price, cost } = getMsrpPriceCost(allValues)
  if (price < cost) { return '需高於進貨成本' }
  if (price > msrp) { return '需低於市價' }
  return undefined
}

export const costAmountValidation = (valueInStore, allValues) => {
  const { msrp, price, cost } = getMsrpPriceCost(allValues)
  const isNewListing = getIsNewListing(allValues)
  if (isNewListing && cost > price) { return '需低於購物中心售價' }
  if (cost > msrp) { return '需低於市價' }
  return undefined
}

export const stockValidation = (inputStock, values) => {
  const { product } = values
  const cost = _.toNumber(product.cost)
  const stock = _.toNumber(inputStock)
  // 備貨數量 >= 0; 成本>5萬-數量<15、成本>3萬-數量<50、成本>1萬-數量<100、成本>5千-數量<300、成本>3千-數量<500、成本>0元-數量<1000
  if (stock < 0) {
    return '備貨數量需大於0'
  }
  if (cost >= 50000) {
    const stockLimit = 15
    return numericality({ '<=': stockLimit, msg: `成本≥5萬-數量≤${stockLimit}` })(stock)
  }
  if (cost >= 30000) {
    const stockLimit = 50
    return numericality({ '<=': stockLimit, msg: `成本≥3萬-數量≤${stockLimit}` })(stock)
  }
  if (cost >= 10000) {
    const stockLimit = 100
    return numericality({ '<=': stockLimit, msg: `成本≥1萬-數量≤${stockLimit}` })(stock)
  }
  if (cost >= 5000) {
    const stockLimit = 300
    return numericality({ '<=': stockLimit, msg: `成本≥5千-數量≤${stockLimit}` })(stock)
  }
  if (cost >= 3000) {
    const stockLimit = 500
    return numericality({ '<=': stockLimit, msg: `成本≥3千-數量≤${stockLimit}` })(stock)
  }
  if (cost > 0) {
    const stockLimit = 1000
    return numericality({ '<=': stockLimit, msg: `成本≥0元-數量≤${stockLimit}` })(stock)
  }
  return null
}

export const validateBarcode = value => {
  return (value && !isValidBarcode(value))
    ? '國際條碼輸入格式錯誤'
    : null
}

/** 商品材積驗證， get error for `_productSize` field **/
export const validateAllSize = ({
  shipTypeId,
  skipRequired,
  productSizeValues,
  applianceContract,
  isInstallRequired,
  singleSizeDimensionError,
  isNeedRecycle
}) => {
  const hasFilledSomeSizeField = Object
    .values(productSizeValues)
    .some(value => value !== '')

  /**
   * 如果有長寬高有填任何一個，
   * validation 檢查就不可以略過
   */
  if (skipRequired && !hasFilledSomeSizeField) {
    return undefined
  }

  if (singleSizeDimensionError) {
    return singleSizeDimensionError
  }

  if (shipTypeId === PRODUCT_SHIP_TYPE.EXPRESS_24HR) {
    const totalVolume = calculateTotalVolume(productSizeValues)
    const isSpecialLimitRequired = isNeedRecycle || isInstallRequired
    if (applianceContract && isSpecialLimitRequired) {
      if (totalVolume > LARGE_VOLUME_RULE.INSTALL_SIZE_SUM) {
        return `長+寬+高需 <= ${LARGE_VOLUME_RULE.INSTALL_SIZE_SUM} cm`
      }
    } else {
      if (totalVolume > LARGE_VOLUME_RULE.SIZE_SUM) {
        return `長+寬+高需 <= ${LARGE_VOLUME_RULE.SIZE_SUM} cm`
      }
    }
  }
}

export const validateSingleSizeDimension = ({
  value = '',
  isEmpty = false,
  isHeight = false,
  skipRequired = false,
  applianceContract = false,
  // 預設需安裝是不打勾的
  isInstallRequired = false,
  isNeedRecycle = false,
  shipTypeId
}) => {
  if (skipRequired && isEmpty) { return undefined }

  if (isEmpty || _.isNil(value)) { return '請輸入商品材積' }

  const numberValue = _.toNumber(value)
  if (!numberValue > 0) { return '請輸入正整數' }

  const isSpecialLimitRequired = (isNeedRecycle || isInstallRequired)
  // 只有快速到貨限制商品尺寸
  if (shipTypeId === PRODUCT_SHIP_TYPE.EXPRESS_24HR) {
    if (applianceContract && isSpecialLimitRequired) {
      if (isHeight) {
        if (numberValue > LARGE_VOLUME_RULE.INSTALL_SIZE_HEIGHT) {
          return `高必須 <= ${LARGE_VOLUME_RULE.INSTALL_SIZE_HEIGHT}公分`
        }
      } else {
        if (numberValue > LARGE_VOLUME_RULE.INSTALL_SIZE_SINGLE) {
          return `長寬必須 <= ${LARGE_VOLUME_RULE.INSTALL_SIZE_SINGLE}公分`
        }
      }
    } else {
      if (numberValue > LARGE_VOLUME_RULE.SIZE_SINGLE) {
        return `材積單邊必須 <= ${LARGE_VOLUME_RULE.SIZE_SINGLE}公分`
      }
    }
  }
}

/** 長寬高重量的 validation */
export const validateProductSize = values => {
  const errors = {}

  const shipTypeId = getShipTypeIdFromAllValues(values)
  const applianceContract = signedApplianceContract(values)

  // mall info
  // 快速到貨及直店配才有長寬高重量
  const { HOME_STORE, EXPRESS_24HR } = PRODUCT_SHIP_TYPE
  if ([HOME_STORE, EXPRESS_24HR].includes(shipTypeId)) {
    const skipRequired = isSkipRequired(values)

    // @example
    // 'shipTypeFormSection.shipType61.product'
    const productNameFieldByShipType = getScopedFieldNameByShipType(shipTypeId, 'product')
    const scopedProduct = _.get(values, productNameFieldByShipType, {})
    const isNeedRecycle = _.get(values, 'product.isNeedRecycle', false)

    const { isInstallRequired } = scopedProduct

    /** 長寬高各自的 validation */
    const singleSizeErrorSet = new Set()
    PRODUCT_VOLUME_DIMENSION_NAMES.forEach(dimensionName => {
      const value = scopedProduct[dimensionName]

      const isHeight = dimensionName === PRODUCT_VOLUME_DIMENSION.H.VALUE
      const isEmpty = isEmptyStr(value)

      const fieldName = `${productNameFieldByShipType}.${dimensionName}`
      const fieldError = validateSingleSizeDimension({
        value,
        isEmpty,
        isHeight,
        skipRequired,
        applianceContract,
        isInstallRequired,
        isNeedRecycle,
        shipTypeId
      })

      _.set(
        errors,
        fieldName,
        fieldError
      )

      singleSizeErrorSet.add(fieldError)
    })

    const singleSizeDimensionError = Array.from(singleSizeErrorSet)
      .filter(error => !_.isNil(error))
      .join()

    /** 長寬高共用的 _productSize Error 的 validation */
    // @example
    // 'shipTypeFormSection.shipType61.setting._productSize'
    const productSizeValues = _.pick(scopedProduct, PRODUCT_VOLUME_DIMENSION_NAMES)
    const scopedProductSizeFieldName = getScopedFieldNameByShipType(shipTypeId, 'setting._productSize')
    const sharedSizeError = validateAllSize({
      shipTypeId,
      skipRequired,
      productSizeValues,
      applianceContract,
      isInstallRequired,
      singleSizeDimensionError,
      isNeedRecycle
    })
    _.set(
      errors,
      scopedProductSizeFieldName,
      sharedSizeError
    )

    /** `長+寬+高` 超過限制的時候，長寬高三個 field 都要紅框 */
    const msgOfProductSize = _.get(errors, scopedProductSizeFieldName, '')

    if (msgOfProductSize.includes('長+寬+高需 <=')) {
      Object.keys(productSizeValues).forEach(key => {
        const fieldName = `${productNameFieldByShipType}.${key}`
        _.set(
          errors,
          fieldName,
          msgOfProductSize
        )
      })
    }
  }

  return errors
}

export const validatePreserveDays = values => {
  const shipTypeId = getShipTypeIdFromAllValues(values)
  if (shipTypeId === PRODUCT_SHIP_TYPE.EXPRESS_24HR) {
    const productNameFieldByShipType = getScopedFieldNameByShipType(shipTypeId, 'product')
    const currentProduct = _.get(values, productNameFieldByShipType, {})
    const {
      years,
      months,
      days
    } = currentProduct
    const isPreserveDaysExist = !!(years || months || days)
    if (!isPreserveDaysExist) {
      return _.set(
        {},
        getScopedFieldNameByShipType(shipTypeId, 'setting._preserveDays'),
        '請輸入保存天數'
      )
    }
    const totalPreserveDays = calculateDaysFromDateType(currentProduct)
    if (totalPreserveDays > FIELD_MAX_NUM_VAL.PRESERVE_DAYS) {
      return _.set(
        {},
        getScopedFieldNameByShipType(shipTypeId, 'setting._preserveDays'),
        `保存天數不能超過 ${FIELD_MAX_NUM_VAL.PRESERVE_DAYS} 天`
      )
    }
  }
  return null
}

// 驗證遊戲軟體情節，如果子站是28而且選擇電玩情節則強制驗證，忽略草稿功能
export const validateProductGameContents = values => {
  const gameContents = _.get(values, 'product.gameContents')
  const subStationId = _.get(values, 'subStationId')
  const errors = {
    product: {
      gameContents: _.isEmpty(gameContents) && `${CATEGORY_TYPE.SUB}${SUBSTATION_ID.GAME}` === subStationId ? '請選擇軟體情節' : null
    }
  }

  return errors
}

/** 屬性設定驗證 **/
export const validateSpec = values => {
  const errors = {}

  const { subStationId, product = {} } = values
  const { catItemId, struDataAttrClusterId } = product
  const productName = _.get(product, 'name', '') || ''
  const curProductNameLength = productName ? productName.length : 0
  const curShowExpiringAddon = _.get(values, 'setting.showExpiringAddon', false)
  const proposalType = _.get(values, 'setting.proposalType', '')
  const curProductNameLengthMax = getProductNameLengthMax(curShowExpiringAddon, proposalType)
  const activeTransientData = getActiveTransientData(values)
  if (activeTransientData) {
    const activeTransientField = `${getAttrTransientKey(subStationId, catItemId, struDataAttrClusterId)}`
    const transientSpecName = `${activeTransientField}.spec`
    const spec = _.get(activeTransientData, 'spec', [])
    const allSpecSelectedOpts = _.map(spec, localSpec => _.get(localSpec, 'selectedOption') || {})

    // 驗證transient區域的屬性選項
    spec.forEach((localSpec, idx, specArray) => {
      const level = idx + 1
      const { selectedOption, selectedItemOptions = [] } = localSpec
      const lengthUsedByFirstLayer = level === 1 ? 0 : getLengthUsedByFirstLayer(_.get(specArray, '[0]selectedItemOptions', []))
      const specItemExceedLength = getSpecItemExceedLength(curProductNameLengthMax, level, lengthUsedByFirstLayer, curProductNameLength)
      const specIndexField = `${transientSpecName}[${idx}]`
      const specErrors = []
      if (_.isEmpty(selectedOption)) {
        specErrors.push('請選擇屬性')
      }
      if (hasDuplicateSpec(allSpecSelectedOpts)) {
        specErrors.push('屬性不可重複')
      }

      const specItemErrors = []
      if (_.isEmpty(selectedItemOptions)) {
        specItemErrors.push('請選擇屬性項目')
      }
      if (hasDuplicateSpec(selectedItemOptions)) {
        specItemErrors.push('項目名稱不可相同，請調整項目名稱')
      }
      if (_.some(selectedItemOptions, item => _.get(item, 'value', '').length > specItemExceedLength)) {
        specItemErrors.push(`"${SPEC_ATTRIBUTE_VALIDATE_RULE_TEXT}"，需在${curProductNameLengthMax}字以內，請調整項目名稱`)
      }

      // 若有錯誤則賦值
      if (!_.isEmpty(specErrors)) {
        _.set(errors, `${specIndexField}.selectedOption`, specErrors)
      }
      if (!_.isEmpty(specItemErrors)) {
        _.set(errors, `${specIndexField}.selectedItemOptions`, specItemErrors)
      }
    })
  }

  return errors
}

export const validateWarehouseProductName = values => {
  const errors = {}
  const activeTransientData = getActiveTransientData(values)
  const { withSpecLevel: hasSpecLevel } = determineSpecLevel(activeTransientData.hasSpecLevel)
  // 有屬性才有可能出現重複, 所以只在此時檢查
  if (activeTransientData && hasSpecLevel) {
    const { subStationId, product = {} } = values
    const { catItemId, struDataAttrClusterId } = product
    const activeTransientField = `${getAttrTransientKey(subStationId, catItemId, struDataAttrClusterId)}`
    const transientTableField = `${activeTransientField}.table.withLevel`
    const models = _.get(activeTransientData, 'table.withLevel.models', [])
    const uniqueNames = []
    _.forEach(models, (model, modelIdx) => {
      const items = model.items || []
      _.forEach(items, (item, itemIdx) => {
        if (_.includes(uniqueNames, item.warehouseProductName)) { // 已有相同名稱存在
          _.set(errors,
            `${transientTableField}.models[${modelIdx}].items[${itemIdx}].warehouseProductName`,
            '同賣場不同商品之"進倉用標籤/品名"不可重複')
        } else {
          uniqueNames.push(item.warehouseProductName)
        }
      })
    })
  }
  return errors
}

/* 市價售價成本最大值驗證 */
export const amountValidationBy = (amountType = '') => (value, allValues) => {
  const shipTypeId = getShipTypeIdFromAllValues(allValues)
  const cap = getPriceCap(shipTypeId, amountType)
  const validation = numericality({
    '<=': cap,
    msg: `上限為${getDollarFormatted(cap)}`
  })
  const validatedResult = validation(value)

  return validatedResult
}

/** newProduct 且選中 '贈品' cat 時，進貨成本必須為 0 */
export const giftCostValidation = (value, allValues) => {
  const isSelectedOptionGift = _.get(allValues, 'setting.isSelectedOptionsGift', false)
  if (isSelectedOptionGift) {
    return numericality({
      '=': 0,
      msg: '新增商品為贈品，進貨成本必須為 0'
    })(value)
  }
}

/** newProduct 可以接受 0，newListing 不可以 */
export const positivePriceValidation = (value, allValues) => {
  const proposalType = _.get(allValues, 'setting.proposalType', '')
  const isNewListing = isNewListingProposal(proposalType)
  const options = isNewListing
    ? {
      '>': 0,
      msg: '請輸入正整數'
    }
    : {
      '>=': 0,
      msg: '請輸入大於等於 0 的整數'

    }

  return numericality(options)(value)
}

export const expiredTsValidation = (value, allValues) => {
  if (isMomentOutdated(value, DEADLINE_OPTIONS)) {
    return '審核期限需為提案日＋3~15天'
  }
  return null
}

export const preOrderExpectedShipDateValidation = (value, allValues) => {
  const deliveryTypesFromUserProfile = _.get(allValues, 'setting.user.profile.toggles', [])
  const isIncludedPreOrder = checkSuppilerIncludedPreOrder(deliveryTypesFromUserProfile)
  let preOrderOptions = PRE_ORDER_EXPECTED_SHIP_DATE_OPTIONS
  let maxDate = PRE_ORDER_EXPECTED_SHIP_DATE.SUB_STATION_MAX_DATE
  if (isIncludedPreOrder) {
    preOrderOptions = PRE_ORDER_EXPECTED_SHIP_DATE_SUPPLIER_OPTIONS
    maxDate = PRE_ORDER_EXPECTED_SHIP_DATE.SUPPLIER_MAX_DATE
  }
  if (isMomentOutdated(value, preOrderOptions)) {
    return `預定出貨日期不可小於今日+5，不可大於今日+${maxDate}`
  }
  return null
}

export const onShelvedTsValidation = (value, allValues) => {
  if (isMomentOutdated(value, ON_SHELVED_TS_OPTIONS)) {
    return '開始時間不可小於三個月內，不可大於今日起算10年後'
  }
  return null
}

export const offShelvedTsValidation = (value, allValues) => {
  const onShelvedTs = _.get(allValues, 'listing.onShelvedTs')
  const offShelvedTsOptions = {
    // 結束時間的maxDate是要跟開始時間一致的+10年
    minDate: getDefaultTimezoneMoment(onShelvedTs).format(),
    maxDate: ON_SHELVED_TS_OPTIONS.maxDate
  }
  if (isMomentOutdated(value, offShelvedTsOptions)) {
    return '結束時間不可小於開始時間，不可大於今日起算10年後'
  }
  return null
}

export const subStationIdValidation = (value, allValues) => {
  const subStationOptions = _.get(allValues, 'setting.subStationOptions', [])
  // 檢測是否為可選子站
  if (_.some(subStationOptions, { value })) {
    return null
  }
  return '請重新輸入提案站別'
}

// text attribute 如果為非必填則只在有值時檢查字數長度
export const textAttributeValidation = option => (valueInStore, allValues, props, name) => {
  let result = null
  const textValue = _.isArray(valueInStore) ? valueInStore[0] : valueInStore
  if (textValue) {
    const {
      min,
      max
    } = option

    if (!_.isNil(min)) {
      result = length({ min })(textValue)
    }
    if (!_.isNil(max) && _.isNil(result)) {
      result = length({ max, msg: '字數超過上限' })(textValue)
    }
  }

  return result
}

/** 是 PM，申請低毛利率賣場的 checkbox 需要檢查 */
export const applyLowGpmValidation = shouldApplyLowGpm => (value, allValues) => {
  if (shouldApplyLowGpm) {
    if (!value) {
      return '賣場毛利率 < 子站毛利率，需申請低毛利率賣場'
    }
  }
}

/** 賣場名稱長度驗證 */
export const productNameLengthValidation = (value, allValues) => {
  const showExpiringAddon = _.get(allValues, 'setting.showExpiringAddon', false)
  const proposalType = _.get(allValues, 'setting.proposalType', _.get(allValues, 'type', ''))
  const productNameLengthMax = getProductNameLengthMax(showExpiringAddon, proposalType)
  const lengthValidation = length({
    max: productNameLengthMax,
    msg: '字數超過上限'
  })
  return lengthValidation(value)
}

/** 超取數量限制 */
export const cvsQtyLimitValidation = (value, allValues) => {
  const cvsQtyLimitMax = _.get(allValues, 'setting.cvsQtyLimitMax', 1)
  const limitValidation = numericality({
    '<=': cvsQtyLimitMax,
    msg: `上限為 ${cvsQtyLimitMax}`
  })
  return limitValidation(value)
}

/** 特色標題驗證 */
export const featureTitleValidation = (value, allValues) => {
  return FEATURE_TITLE_INVALID_REGEX.test(value) ? '特色標題僅接受逗號、空白、%、/、-及注音符號' : null
}

/** 商品詳情外站圖片檢查 */
export const productCopyValidation = (imagePathPrefixes) => (valueInEditor = '', allValues) => {
  const isUploadExceedLimitImage = valueInEditor.match(/<img.*alt="Error:/)
  if (isUploadExceedLimitImage) {
    return (
      <p className={FORM_ERROR_CLASS_NAME}>
        外站圖片上傳失敗
      </p>
    )
  }

  const externalImgArr = getExternalImages(imagePathPrefixes)(valueInEditor)
  return externalImgArr !== null ? _.concat(['商品詳情有尚未轉換完成的圖片，請稍候'], externalImgArr) : null
}

export const couponTitleLengthMax = max => value => length({ max, msg: '字數超過上限' })(value)
